import React, { useState, useEffect, useMemo, useContext } from 'react';

// ICONS
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react'; // Importer les icônes et ajouter les icônes de pagination

// COMPONENTS
import TooltipComponent from 'components/tooltipComponent';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

// Fonctions utilitaires pour les badges et actions (similaires à l'exemple)
function getBadgeColor(variant) {
  switch (variant) {
    case "success":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "warn":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
    case "error":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    case "info":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "primary":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
    case "neutral":
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
  }
}

function getIconHoverColor(variant) {
  switch (variant) {
    case "success":
      return "hover:bg-green-200 dark:hover:bg-green-700";
    case "warn":
      return "hover:bg-yellow-200 dark:hover:bg-yellow-700";
    case "error":
      return "hover:bg-red-200 dark:hover:bg-red-700";
    case "info":
      return "hover:bg-blue-200 dark:hover:bg-blue-700";
    default:
      return "hover:bg-purple-200 dark:hover:bg-purple-700";
  }
}

// Fonction utilitaire pour copier dans le presse-papier
const copyToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    // Pour les contextes sécurisés (HTTPS)
    return navigator.clipboard.writeText(text);
  } else {
    // Fallback pour les contextes non sécurisés
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      textArea.remove();
      return Promise.resolve();
    } catch (err) {
      textArea.remove();
      return Promise.reject(err);
    }
  }
};

function renderCell(value, column, row, addToast) {
  // Fonction utilitaire pour afficher une valeur vide
  const renderEmptyValue = () => (
    <span className="text-sm text-gray-400 dark:text-gray-500 italic">Non renseigné</span>
  );

  // Vérification initiale pour les valeurs nulles ou vides
  if (value === null || value === undefined || value === '') {
    return renderEmptyValue();
  }

  switch (column.type) {
    case "image":
      // value est attendu comme un objet { src, alt }
      const defaultImage = "https://placehold.co/40x40?text=No+Image";
      if (!value.src) return renderEmptyValue();
      return (
        <div className="flex items-center">
          <img
            src={value.src || defaultImage}
            alt={value.alt || "image par défaut"}
            className="size-10 object-cover rounded-lg"
          />
        </div>
      );
    case "picture":
      // value est attendu comme un objet { src, alt }
      const defaultPicture = "https://placehold.co/40x40?text=No+Image";
      if (!value.src) return renderEmptyValue();
      return (
        <div className="flex items-center">
          <img
            src={value.src || defaultPicture}
            alt={value.alt || "image par défaut"}
            className="size-10 object-cover rounded-full min-w-[40px] min-h-[40px]"
          />
        </div>
      );
    case "profil":
      // value est attendu comme un objet { src, alt, username }
      const defaultProfil = "https://placehold.co/40x40?text=No+Image";
      if (!value.src || !value.username) return renderEmptyValue();
      return (
        <TooltipComponent title={value.username} placement="top">
          <button
            onClick={() => {
              copyToClipboard(value.username)
                .then(() => {
                  addToast('Succès', 'Copié dans votre presse papier', null, null, 'success');
                })
                .catch(() => {
                  addToast('Erreur', 'Impossible de copier dans le presse papier', null, null, 'error');
                });
            }}
            className="flex items-center focus:outline-none"
          >
            <img
              src={value.src || defaultProfil}
              alt={value.alt || "image par défaut"}
              className="size-10 object-cover rounded-full cursor-pointer hover:opacity-80 transition-opacity min-w-[40px] min-h-[40px]"
            />
          </button>
        </TooltipComponent>
      );
    case "badge":
      // value est attendu comme un objet { text, variant, tooltip }
      if (!value || typeof value !== "object" || !value.text) return renderEmptyValue();
      const { text, variant, tooltip } = value;
      const badgeColorClass = getBadgeColor(variant);
      const badgeContent = (
        <span className={`text-xs font-semibold px-4 py-2 rounded-lg ${badgeColorClass} min-w-[100px] inline-block text-center`}>
          {text}
        </span>
      );
      
      return tooltip ? (
        <TooltipComponent title={tooltip} placement="top">
          {badgeContent}
        </TooltipComponent>
      ) : badgeContent;
    case "currency":
      if (typeof value !== "number") return renderEmptyValue();
      return `$${value.toFixed(2)}`;
    case "link":
      if (!value) return renderEmptyValue();
      return (
        <TooltipComponent title={value} placement="top">
          <a 
            href={value}
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline block min-w-[150px] max-w-[200px] truncate"
          >
            {value}
          </a>
        </TooltipComponent>
      );
    case "action":
      // value est attendu comme un tableau d'objets { icon: IconComponent, variant, onClick, label }
      if (!Array.isArray(value) || value.length === 0) return renderEmptyValue();
      return (
        <div className="flex items-center space-x-2 min-w-[120px]">
          {value.map((item, index) => {
            const { icon: Icon, variant, onClick, label } = item;
            const hoverColorClass = getIconHoverColor(variant);
            return (
              <button
                key={index}
                onClick={() => onClick(row)}
                title={label}
                className={`p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-blanc ${hoverColorClass} transition-colors`}
              >
                <Icon size={18} />
              </button>
            );
          })}
        </div>
      );
    case "date":
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return renderEmptyValue();
        return <span className="text-sm text-gray-900 dark:text-blanc min-w-[100px] inline-block">{date.toLocaleDateString('fr-FR')}</span>;
      } catch (error) {
        console.error("Erreur lors du formatage de la date:", error);
        return renderEmptyValue();
      }
    case "datetime":
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return renderEmptyValue();
        return <span className="text-sm text-gray-900 dark:text-blanc min-w-[150px] inline-block">{date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>;
      } catch (error) {
        console.error("Erreur lors du formatage de la datetime:", error);
        return renderEmptyValue();
      }
    case "textarea":
      if (!value) return renderEmptyValue();
      return (
        <TooltipComponent title={value} placement="top">
          <div className="min-w-[300px] max-h-[50px] overflow-y-auto">
            <p className="text-sm text-gray-900 dark:text-blanc whitespace-pre-wrap">
              {value}
            </p>
          </div>
        </TooltipComponent>
      );
    case "color":
      if (!value) return renderEmptyValue();
      return (
        <TooltipComponent title={value} placement="top">
          <div className="flex items-center space-x-2">
            <div 
              className="size-6 rounded-full" 
              style={{ backgroundColor: value }}
            />
            <span className="text-sm text-gray-900 dark:text-blanc">{value}</span>
          </div>
        </TooltipComponent>
      );
    case "text":
      if (!value) return renderEmptyValue();
      // Si la colonne est 'title' ou 'description', on applique l'ellipsis + tooltip
      if (column.key === 'title' || column.key === 'description') {
        return (
          <TooltipComponent title={value} placement="top">
            <span className="block max-w-[150px] truncate cursor-pointer text-sm text-gray-900 dark:text-blanc">
              {value}
            </span>
          </TooltipComponent>
        );
      }
      return <span className="text-sm text-gray-900 dark:text-blanc">{value}</span>;
    default:
      if (!value) return renderEmptyValue();
      return <span className="text-sm text-gray-900 dark:text-blanc">{value}</span>;
  }
}


const TableComponent = ({ 
  columns = [], 
  rows = [], 
  emptyText = "Aucune donnée disponible", 
  defaultSortConfig = { key: null, direction: 'descending' },
  enablePagination = false,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100]
}) => {
  const [hasData, setHasData] = useState(false);
  const [sortConfig, setSortConfig] = useState(defaultSortConfig);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const { addToast } = useContext(ToastContext);

  useEffect(() => {
    setHasData(rows.length > 0);
    // Réinitialiser la page courante seulement si les données sont vides ou si on dépasse le nombre total de pages
    const newTotalPages = Math.ceil(rows.length / pageSize);
    if (rows.length === 0 || currentPage > newTotalPages) {
      setCurrentPage(1);
    }
  }, [rows, pageSize, currentPage]);

  const sortedRows = useMemo(() => {
    let sortableItems = [...rows];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Gestion spécifique pour les dates
        if (columns.find(col => col.key === sortConfig.key)?.type === 'date') {
          const dateA = new Date(aValue).getTime();
          const dateB = new Date(bValue).getTime();
          // Gérer les dates invalides en les plaçant à la fin/début selon la direction
          if (isNaN(dateA) && isNaN(dateB)) return 0;
          if (isNaN(dateA)) return sortConfig.direction === 'ascending' ? 1 : -1;
          if (isNaN(dateB)) return sortConfig.direction === 'ascending' ? -1 : 1;

          if (dateA < dateB) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (dateA > dateB) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        }

        // Gestion spécifique pour les datetimes (similaire aux dates)
        if (columns.find(col => col.key === sortConfig.key)?.type === 'datetime') {
          const dateA = new Date(aValue).getTime();
          const dateB = new Date(bValue).getTime();
          if (isNaN(dateA) && isNaN(dateB)) return 0;
          if (isNaN(dateA)) return sortConfig.direction === 'ascending' ? 1 : -1;
          if (isNaN(dateB)) return sortConfig.direction === 'ascending' ? -1 : 1;

          if (dateA < dateB) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (dateA > dateB) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        }

        // Gestion spécifique pour les badges
        if (columns.find(col => col.key === sortConfig.key)?.type === 'badge') {
          const textA = aValue?.text || '';
          const textB = bValue?.text || '';
          
          if (textA < textB) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (textA > textB) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        }

        // Tri standard pour les autres types (simplifié, peut nécessiter ajustement)
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [rows, sortConfig, columns]);

  // Calcul des données paginées
  const paginatedRows = useMemo(() => {
    if (!enablePagination) return sortedRows;
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedRows.slice(startIndex, endIndex);
  }, [sortedRows, currentPage, pageSize, enablePagination]);

  // Calcul du nombre total de pages
  const totalPages = useMemo(() => {
    if (!enablePagination) return 1;
    return Math.ceil(sortedRows.length / pageSize);
  }, [sortedRows.length, pageSize, enablePagination]);

  // Gestionnaires d'événements pour la pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // Réinitialiser à la première page lors du changement de taille
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } else if (sortConfig.key === key && sortConfig.direction === 'descending') {
      // Optionnel: Revenir à l'état non trié ou cycler vers ascendant
       direction = 'ascending'; // Ou retourner à l'état initial: setSortConfig({ key: null, direction: 'ascending' });
    }
    setSortConfig({ key, direction });
  };

  if (!columns.length) {
    return <p>La configuration des colonnes est manquante.</p>;
  }

  return (
    <div className="w-full h-full min-h-[400px] max-h-[700px] bg-blanc dark:bg-dark-secondary border border-gray-200 dark:border-gray-700 rounded-lg shadow flex flex-col">
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          <table className="w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-dark sticky top-0 z-10">
            <tr className="border-b border-gray-200 dark:border-gray-600">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap ${col.type === 'date' || col.type === 'datetime' || col.type === 'badge' ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                  onClick={() => (col.type === 'date' || col.type === 'datetime' || col.type === 'badge') && requestSort(col.key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{col.label}</span>
                    {sortConfig.key === col.key ? (
                      sortConfig.direction === 'ascending' ? (
                        <ChevronUp className="ml-1 size-4 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="ml-1 size-4 flex-shrink-0" />
                      )
                    ) : (col.type === 'date' || col.type === 'datetime' || col.type === 'badge') ? (
                      <ChevronDown className="ml-1 size-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    ): null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {hasData ? (
              paginatedRows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-6 py-4"
                    >
                      {renderCell(row[col.key], col, row, addToast)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="py-4 text-center text-gray-500 dark:text-gray-400 text-lg italic">{emptyText}</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {enablePagination && hasData && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 sm:px-6 py-4 bg-gray-50 dark:bg-dark border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start">
              <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Lignes par page:</span>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-blanc dark:bg-dark-secondary text-gray-700 dark:text-gray-300 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Total: {sortedRows.length} ligne{sortedRows.length > 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
            </button>
            
            <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Page {currentPage} sur {totalPages}
            </span>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComponent; 